import { useState, useMemo, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSources, useCreateSource } from '../../hooks/useSource';
import { useSourceTypes } from '../../hooks/useSourceTypes';
import { useIsInDraftMode } from '../../hooks/useDraftMode';
import { getSourceCategory, type Source, type SourceCategory, type SourceType } from '../../services/source.service';
import { SourceIcon } from '../../components/SourceIcon';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '../../components/ui/breadcrumb';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../components/ui/pagination';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Loader2,
  Search,
  FileSpreadsheet,
  Database,
  Plug,
  Settings,
  Plus,
  ArrowLeft,
  ChevronRight,
  TableIcon,
  Rows3,
} from 'lucide-react';
import { summarizeNumber } from '../../utils/formatting';


function getSourceTypeBadge(sourceType: string) {
  const category = getSourceCategory(sourceType);
  switch (category) {
    case 'flat_file':
      return (
        <Badge variant="outline" className="border-green-500 text-green-700 dark:text-green-400 dark:border-green-400">
          Flat File
        </Badge>
      );
    case 'snowflake':
      return (
        <Badge variant="outline" className="border-blue-500 text-blue-700 dark:text-blue-400 dark:border-blue-400">
          Snowflake
        </Badge>
      );
    case 'managed_integration':
      return (
        <Badge variant="secondary" className="whitespace-nowrap">
          Managed Integration
        </Badge>
      );
  }
}

function getSourceStatus(source: Source): { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' } {
  // Check for fivetran sources
  if (source.fivetran?.connector?.status) {
    const syncState = source.fivetran.connector.status.sync_state;
    const setupState = source.fivetran.connector.status.setup_state;

    if (setupState === 'incomplete') {
      return { label: 'Needs Setup', variant: 'outline' };
    }

    if (syncState === 'syncing') {
      return { label: 'Syncing', variant: 'default' };
    }
    if (syncState === 'paused') {
      return { label: 'Paused', variant: 'secondary' };
    }
    if (syncState === 'scheduled') {
      return { label: 'Scheduled', variant: 'outline' };
    }
    return { label: 'Active', variant: 'default' };
  }

  // Check for meltano/pliable sources
  if (source.sync_info) {
    const status = source.sync_info.status;
    if (status === 'SYNCING') {
      return { label: 'Syncing', variant: 'default' };
    }
    if (status === 'QUEUED') {
      return { label: 'Queued', variant: 'outline' };
    }
    if (!source.sync_info.sync_schedule?.enabled) {
      return { label: 'Paused', variant: 'secondary' };
    }
    return { label: 'Active', variant: 'default' };
  }

  // For flat files or snowflake tables
  if (source.last_loaded) {
    return { label: 'Loaded', variant: 'default' };
  }

  return { label: 'Not Configured', variant: 'outline' };
}

function formatLastSync(source: Source): string {
  // Check fivetran
  if (source.fivetran?.connector?.status?.sync_state) {
    // Fivetran doesn't give us a simple last_sync timestamp in our model
    return '-';
  }

  // Check meltano
  if (source.sync_info?.last_sync_completed_at) {
    return new Date(source.sync_info.last_sync_completed_at).toLocaleDateString();
  }

  // Check last_loaded
  if (source.last_loaded) {
    return new Date(source.last_loaded).toLocaleDateString();
  }

  return 'Never';
}

type DialogStep = 'select-category' | 'select-connector' | 'enter-name';

function NewSourceDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const createSource = useCreateSource();
  const { data: sourceTypes, isLoading: loadingTypes } = useSourceTypes();

  const [step, setStep] = useState<DialogStep>('select-category');
  const [selectedCategory, setSelectedCategory] = useState<SourceCategory | null>(null);
  const [selectedConnector, setSelectedConnector] = useState<SourceType | null>(null);
  const [sourceName, setSourceName] = useState('');
  const [connectorSearch, setConnectorSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const managedIntegrations = useMemo(() => {
    if (!sourceTypes) return [];
    return sourceTypes.filter((st) => {
      const cat = getSourceCategory(st.id);
      return cat === 'managed_integration';
    });
  }, [sourceTypes]);

  const filteredConnectors = useMemo(() => {
    if (!connectorSearch.trim()) return managedIntegrations;
    const q = connectorSearch.toLowerCase();
    return managedIntegrations.filter(
      (c) =>
        c.title.toLowerCase().includes(q) ||
        c.description?.toLowerCase().includes(q)
    );
  }, [managedIntegrations, connectorSearch]);

  function reset() {
    setStep('select-category');
    setSelectedCategory(null);
    setSelectedConnector(null);
    setSourceName('');
    setConnectorSearch('');
    setError(null);
  }

  function handleOpenChange(val: boolean) {
    if (!val) reset();
    onOpenChange(val);
  }

  function handleCategorySelect(category: SourceCategory) {
    setSelectedCategory(category);
    if (category === 'managed_integration') {
      setStep('select-connector');
    } else {
      setStep('enter-name');
    }
  }

  function handleConnectorSelect(connector: SourceType) {
    setSelectedConnector(connector);
    setStep('enter-name');
  }

  function handleBack() {
    setError(null);
    if (step === 'enter-name') {
      setSourceName('');
      if (selectedCategory === 'managed_integration') {
        setSelectedConnector(null);
        setStep('select-connector');
      } else {
        setSelectedCategory(null);
        setStep('select-category');
      }
    } else if (step === 'select-connector') {
      setSelectedCategory(null);
      setConnectorSearch('');
      setStep('select-category');
    }
  }

  async function handleCreate() {
    if (!sourceName.trim()) return;
    setError(null);

    let sourceType = '';
    if (selectedCategory === 'flat_file') {
      sourceType = 'flat_file';
    } else if (selectedCategory === 'snowflake') {
      sourceType = 'snowflake_table';
    } else if (selectedConnector) {
      sourceType = selectedConnector.id;
    }

    try {
      const newSource = await createSource.mutateAsync({
        name: sourceName.trim(),
        type: sourceType,
      });
      handleOpenChange(false);
      navigate(`/sources/${newSource.id}`);
    } catch (err: any) {
      setError(err?.message || 'Failed to create source');
    }
  }

  const isCreating = createSource.isPending;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]" hideClose={isCreating}>
        {step === 'select-category' && (
          <>
            <DialogHeader>
              <DialogTitle>New Source</DialogTitle>
              <DialogDescription>
                Choose the type of data source you want to add.
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="flex flex-col gap-2 py-2">
              <button
                className="flex items-center gap-3 p-3 rounded-md border border-border hover:bg-accent text-left transition-colors"
                onClick={() => handleCategorySelect('flat_file')}
              >
                <FileSpreadsheet className="h-5 w-5 text-green-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">Flat File</div>
                  <div className="text-xs text-muted-foreground">Upload CSV or Excel files</div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              </button>
              <button
                className="flex items-center gap-3 p-3 rounded-md border border-border hover:bg-accent text-left transition-colors"
                onClick={() => handleCategorySelect('snowflake')}
              >
                <Database className="h-5 w-5 text-blue-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">Snowflake</div>
                  <div className="text-xs text-muted-foreground">Read data from an existing Snowflake table</div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              </button>
              <button
                className="flex items-center gap-3 p-3 rounded-md border border-border hover:bg-accent text-left transition-colors"
                onClick={() => handleCategorySelect('managed_integration')}
              >
                <Plug className="h-5 w-5 text-purple-600 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">Managed Integration</div>
                  <div className="text-xs text-muted-foreground">Connect to SaaS apps, databases, and more</div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              </button>
            </DialogBody>
          </>
        )}

        {step === 'select-connector' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <button onClick={handleBack} className="hover:bg-accent rounded p-0.5 transition-colors">
                  <ArrowLeft className="h-4 w-4" />
                </button>
                Select Connector
              </DialogTitle>
              <DialogDescription>
                Choose a managed integration connector.
              </DialogDescription>
            </DialogHeader>
            <DialogBody>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search connectors..."
                  value={connectorSearch}
                  onChange={(e) => setConnectorSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="max-h-[300px] overflow-y-auto flex flex-col gap-1">
                {loadingTypes ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredConnectors.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No connectors found.
                  </p>
                ) : (
                  filteredConnectors.map((connector) => (
                    <button
                      key={connector.id}
                      className="flex items-center gap-3 p-2.5 rounded-md hover:bg-accent text-left transition-colors"
                      onClick={() => handleConnectorSelect(connector)}
                    >
                      {connector.icon_path ? (
                        <img src={connector.icon_path} alt="" className="h-5 w-5 flex-shrink-0 object-contain" />
                      ) : (
                        <Plug className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{connector.title}</div>
                        {connector.description && (
                          <div className="text-xs text-muted-foreground truncate">{connector.description}</div>
                        )}
                      </div>
                      <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                    </button>
                  ))
                )}
              </div>
            </DialogBody>
          </>
        )}

        {step === 'enter-name' && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <button onClick={handleBack} className="hover:bg-accent rounded p-0.5 transition-colors" disabled={isCreating}>
                  <ArrowLeft className="h-4 w-4" />
                </button>
                Name Your Source
              </DialogTitle>
              <DialogDescription>
                {selectedCategory === 'flat_file' && 'New flat file source'}
                {selectedCategory === 'snowflake' && 'New Snowflake source'}
                {selectedCategory === 'managed_integration' && selectedConnector && `New ${selectedConnector.title} source`}
              </DialogDescription>
            </DialogHeader>
            <DialogBody className="space-y-2 py-2">
              <Label htmlFor="source-name">Source Name</Label>
              <Input
                id="source-name"
                placeholder="e.g. My Sales Data"
                value={sourceName}
                onChange={(e) => setSourceName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && sourceName.trim() && !isCreating) {
                    handleCreate();
                  }
                }}
                disabled={isCreating}
                autoFocus
              />
              {error && (
                <p className="text-sm text-destructive">{error}</p>
              )}
            </DialogBody>
            <DialogFooter>
              <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isCreating}>
                Cancel
              </Button>
              <Button onClick={handleCreate} disabled={!sourceName.trim() || isCreating}>
                {isCreating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Create Source
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Sources() {
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [newSourceOpen, setNewSourceOpen] = useState(false);
  const pageSize = 50;
  const isInDraftMode = useIsInDraftMode();

  const getShape = useCallback((source: Source) => {
    return isInDraftMode ? source.draft_shape : source.prod_shape;
  }, [isInDraftMode]);

  const { data, isLoading, error } = useSources({ page, pageSize });

  // Filter and sort sources with search
  const sources = useMemo(() => {
    const allSources = data?.records || [];

    // Apply search filter
    const filtered = searchQuery.trim()
      ? allSources.filter((source: Source) => {
          const name = (source.name || '').toLowerCase();
          const description = (source.description || '').toLowerCase();
          const type = (source.type || '').toLowerCase();
          const query = searchQuery.toLowerCase();
          return name.includes(query) || description.includes(query) || type.includes(query);
        })
      : allSources;

    // Sort alphabetically by name
    return filtered.slice().sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }, [data?.records, searchQuery]);

  const totalPages = data?.total_pages || 1;

  const { totalTables, totalRecords } = useMemo(() => {
    let tables = 0;
    let records = 0;
    for (const s of sources) {
      const shape = getShape(s);
      tables += shape?.total_tables || 0;
      records += shape?.total_source_records || 0;
    }
    return { totalTables: tables, totalRecords: records };
  }, [sources, getShape]);

  return (
    <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: 'calc(100vh)' }}>
      {/* Top Bar */}
      <div className="flex items-center justify-between w-full px-6 py-4 border-b border-border bg-background flex-shrink-0 z-10">
        <div className="flex flex-col gap-2 flex-1 min-w-0">
          {/* Breadcrumbs */}
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbPage>Sources</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
          {/* Page Title */}
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-semibold text-foreground">Data Sources</h1>
            {totalTables > 0 && (
              <Badge variant="secondary" className="gap-1">
                <TableIcon className="h-3 w-3" />
                {summarizeNumber(totalTables)} {totalTables === 1 ? 'table' : 'tables'}
              </Badge>
            )}
            {totalRecords > 0 && (
              <Badge variant="secondary" className="gap-1">
                <Rows3 className="h-3 w-3" />
                {summarizeNumber(totalRecords)} records
              </Badge>
            )}
          </div>
        </div>
        {/* Search + New Source */}
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search sources..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64 pl-9"
            />
          </div>
          <Button onClick={() => setNewSourceOpen(true)}>
            <Plus className="h-4 w-4 mr-1.5" />
            New Source
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-8 min-h-0">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading sources...</p>
            </div>
          </div>
        ) : error ? (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-destructive text-sm">
              Failed to load sources. Please try again.
            </p>
          </div>
        ) : sources.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-muted-foreground">
              {searchQuery ? 'No sources found matching your search.' : 'No data sources configured yet.'}
            </p>
          </div>
        ) : (
          <>
            <div className="rounded-md border">
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[25%]">Name</TableHead>
                    <TableHead className="w-[8%] text-right">Tables</TableHead>
                    <TableHead className="w-[8%] text-right">Records</TableHead>
                    <TableHead className="w-[18%]">Type</TableHead>
                    <TableHead className="w-[12%]">Status</TableHead>
                    <TableHead className="w-[14%]">Last Synced</TableHead>
                    <TableHead className="w-[15%]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sources.map((source: Source) => {
                    const status = getSourceStatus(source);
                    return (
                      <TableRow key={source.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-start gap-2">
                            <div className="flex-shrink-0 mt-0.5">
                              <SourceIcon sourceId={source.id} size={16} />
                            </div>
                            <div className="flex flex-col min-w-0">
                              <Link to={`/sources/${source.id}`} className="hover:underline text-foreground">
                                {source.name}
                              </Link>
                              {source.description && (
                                <span className="text-xs text-muted-foreground font-normal truncate">
                                  {source.description}
                                </span>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm text-right">
                          {getShape(source)?.total_tables ? summarizeNumber(getShape(source)!.total_tables!) : '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm text-right">
                          {getShape(source)?.total_source_records ? summarizeNumber(getShape(source)!.total_source_records!) : '-'}
                        </TableCell>
                        <TableCell>
                          {getSourceTypeBadge(source.type)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={status.variant}>
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {formatLastSync(source)}
                        </TableCell>
                        <TableCell>
                          <Button variant="outline" size="sm" asChild>
                            <Link to={`/sources/${source.id}`}>
                              <Settings className="h-3.5 w-3.5 mr-1.5" />
                              Configure
                            </Link>
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (page > 1) setPage(page - 1);
                        }}
                        style={{ pointerEvents: page === 1 ? 'none' : 'auto', opacity: page === 1 ? 0.5 : 1 }}
                      />
                    </PaginationItem>

                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                      if (
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= page - 1 && pageNum <= page + 1)
                      ) {
                        return (
                          <PaginationItem key={pageNum}>
                            <PaginationLink
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setPage(pageNum);
                              }}
                              isActive={pageNum === page}
                            >
                              {pageNum}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      } else if (pageNum === page - 2 || pageNum === page + 2) {
                        return (
                          <PaginationItem key={`ellipsis-${pageNum}`}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        );
                      }
                      return null;
                    })}

                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (page < totalPages) setPage(page + 1);
                        }}
                        style={{ pointerEvents: page === totalPages ? 'none' : 'auto', opacity: page === totalPages ? 0.5 : 1 }}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </>
        )}
      </div>

      <NewSourceDialog open={newSourceOpen} onOpenChange={setNewSourceOpen} />
    </div>
  );
}
